import React, { useState, useEffect } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import type { Obligation } from "../hooks/useObligations";

const FREQUENCY_UNITS = [
  { value: "WEEKS", label: "weeks" },
  { value: "MONTHS", label: "months" },
  { value: "QUARTERS", label: "quarters" },
  { value: "YEARS", label: "years" },
];

function parseFrequency(frequency: string): {
  interval: number;
  unit: string;
} {
  const parts = frequency.split("_");
  if (parts.length === 2 && !isNaN(Number(parts[0]))) {
    return { interval: Number(parts[0]), unit: parts[1] };
  }
  // Legacy fallback
  const legacy: Record<string, { interval: number; unit: string }> = {
    WEEKLY: { interval: 1, unit: "WEEKS" },
    BIWEEKLY: { interval: 2, unit: "WEEKS" },
    MONTHLY: { interval: 1, unit: "MONTHS" },
    QUARTERLY: { interval: 1, unit: "QUARTERS" },
  };
  return legacy[frequency] || { interval: 1, unit: "MONTHS" };
}

function toFrequencyString(interval: number, unit: string): string {
  return `${interval}_${unit}`;
}

interface ObligationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    obligationType: string;
    frequency: string;
    nextDueAt: string;
  }) => Promise<void>;
  obligation?: Obligation | null;
}

function toDateInputValue(isoString: string | null): string {
  if (!isoString) return "";
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export const ObligationModal: React.FC<ObligationModalProps> = ({
  isOpen,
  onClose,
  onSave,
  obligation,
}) => {
  const isEditing = !!obligation;

  const [formType, setFormType] = useState("");
  const [frequencyInterval, setFrequencyInterval] = useState(1);
  const [frequencyUnit, setFrequencyUnit] = useState("MONTHS");
  const [formDueDate, setFormDueDate] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (obligation) {
        setFormType(obligation.obligationType);
        const parsed = parseFrequency(obligation.frequency);
        setFrequencyInterval(parsed.interval);
        setFrequencyUnit(parsed.unit);
        setFormDueDate(toDateInputValue(obligation.nextDueAt));
      } else {
        setFormType("");
        setFrequencyInterval(1);
        setFrequencyUnit("MONTHS");
        setFormDueDate("");
      }
    }
  }, [isOpen, obligation]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formDueDate || !formType.trim()) return;
    setSaving(true);
    try {
      await onSave({
        obligationType: formType.trim(),
        frequency: toFrequencyString(frequencyInterval, frequencyUnit),
        nextDueAt: new Date(formDueDate).toISOString(),
      });
      onClose();
    } catch {
      // stay open on failure
    } finally {
      setSaving(false);
    }
  };

  const inputClasses =
    "w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? "Edit Obligation" : "New Obligation"}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="obligation-form"
            disabled={!formDueDate || !formType.trim()}
            isLoading={saving}
          >
            {isEditing ? "Save Changes" : "Create"}
          </Button>
        </>
      }
    >
      <form id="obligation-form" onSubmit={handleSubmit} className="p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Type
          </label>
          <input
            type="text"
            value={formType}
            onChange={(e) => setFormType(e.target.value)}
            placeholder="e.g. Monthly Call, Report Delivery, Strategy Session"
            className={inputClasses}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Frequency
          </label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 shrink-0">Every</span>
            <input
              type="number"
              min={1}
              max={52}
              value={frequencyInterval}
              onChange={(e) =>
                setFrequencyInterval(Math.max(1, parseInt(e.target.value) || 1))
              }
              className="w-20 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <select
              value={frequencyUnit}
              onChange={(e) => setFrequencyUnit(e.target.value)}
              className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {FREQUENCY_UNITS.map((u) => (
                <option key={u.value} value={u.value}>
                  {u.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            {isEditing ? "Next Due Date" : "First Due Date"}
          </label>
          <input
            type="date"
            value={formDueDate}
            onChange={(e) => setFormDueDate(e.target.value)}
            required
            className={inputClasses}
          />
        </div>
      </form>
    </Modal>
  );
};
